import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { faqs } from '../../mocks/faqs';

export default function FaqPage() {
  const [openId, setOpenId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const categories = Array.from(new Set(faqs.map(faq => faq.category)));

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleFaq = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-24 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block border border-gray-300 px-5 py-2 rounded-full mb-6">
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Support Center</span>
            </div>
            <h1 className="text-6xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h1>
            
            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <i className="ri-search-line absolute left-6 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for answers..."
                className="w-full h-14 pl-14 pr-6 bg-white border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B6B] focus:border-transparent shadow-sm"
              />
            </div>
          </div>

          {/* FAQs by Category */}
          {categories.map((category) => {
            const categoryFaqs = filteredFaqs.filter(faq => faq.category === category);
            
            if (categoryFaqs.length === 0) return null;

            return (
              <div key={category} className="mb-12">
                <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-6 mt-8">
                  {category}
                </h2>
                
                <div className="space-y-4">
                  {categoryFaqs.map((faq) => (
                    <div
                      key={faq.id}
                      className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300"
                    >
                      <button
                        onClick={() => toggleFaq(faq.id)}
                        className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <h3 className="text-lg font-semibold text-gray-900 pr-4">
                          {faq.question}
                        </h3>
                        <i
                          className={`ri-arrow-down-s-line text-2xl text-gray-400 transition-transform duration-300 flex-shrink-0 ${
                            openId === faq.id ? 'rotate-180' : ''
                          }`}
                        ></i>
                      </button>
                      
                      <div
                        className={`overflow-hidden transition-all duration-300 ${
                          openId === faq.id ? 'max-h-96' : 'max-h-0'
                        }`}
                      >
                        <div className="px-6 pb-6 pt-2">
                          <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {filteredFaqs.length === 0 && (
            <div className="text-center py-16">
              <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-lg text-gray-600">No results found for "{searchQuery}"</p>
              <p className="text-sm text-gray-500 mt-2">Try different keywords or browse all questions</p>
            </div>
          )}

          {/* Contact CTA */}
          <div className="mt-16 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E8E] rounded-3xl p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Still have questions?</h2>
            <p className="text-lg text-white/90 mb-8">
              Can't find the answer you're looking for? Our support team is here to help.
            </p>
            <a
              href="/contact"
              className="inline-block px-10 py-4 bg-white text-[#FF6B6B] font-bold rounded-xl hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
